'use client'

import Link from 'next/link'
import {useState, type ComponentProps, type FocusEvent, type MouseEvent, type TouchEvent} from 'react'

/**
 * `next/link`, prefetching when the student shows intent rather than when the
 * link scrolls into view (T-PERF-01).
 *
 * The dashboard is a page of links — the nav, every shortlist row, every
 * saved search — and default prefetching fetched each target's data as the
 * page loaded: nine extra requests and ~23 KB competing with the page itself
 * on the 3G budget profile. Hover, focus and touch start come early enough
 * that the click still finds the route warm. This is the pattern from
 * Next's prefetching guide: `prefetch={null}` restores the default once the
 * student shows intent.
 */
export function IntentLink({
  href,
  onMouseEnter,
  onFocus,
  onTouchStart,
  ...props
}: Omit<ComponentProps<typeof Link>, 'prefetch'>) {
  const [active, setActive] = useState(false)
  const warm = () => setActive(true)

  return (
    <Link
      {...props}
      href={href}
      prefetch={active ? null : false}
      onMouseEnter={(event: MouseEvent<HTMLAnchorElement>) => {
        warm()
        onMouseEnter?.(event)
      }}
      onFocus={(event: FocusEvent<HTMLAnchorElement>) => {
        warm()
        onFocus?.(event)
      }}
      onTouchStart={(event: TouchEvent<HTMLAnchorElement>) => {
        warm()
        onTouchStart?.(event)
      }}
    />
  )
}

export default IntentLink
